import React, { useMemo } from 'react';

import {
  getColumnLayout,
  getTableCellOwnership,
  getTableCellRichHtml,
  getTableCellStyle,
  normalizeTableShape,
} from './contentLayout';
import TableCellRichContent from './TableCellRichContent';
import './ContentLayout.css';


const renderableLayout = (table, columns) => {
  const raw = table?.column_widths;
  if (
    raw &&
    ['percent', 'px'].includes(raw.unit) &&
    Array.isArray(raw.values) &&
    raw.values.length === columns &&
    raw.values.every((value) => Number.isFinite(Number(value)) && Number(value) > 0)
  ) {
    return {
      unit: raw.unit,
      values: raw.values.map(Number),
    };
  }
  return getColumnLayout(table);
};

const ContentTable = ({
  table,
  className = '',
  activeCell = null,
  selectedRange = null,
  onCellClick,
}) => {
  const shaped = normalizeTableShape(table);
  const ownership = useMemo(
    () => getTableCellOwnership(shaped),
    [shaped]
  );
  const layout = renderableLayout(table, shaped.columns);
  const isPixels = layout.unit === 'px';
  const pixelWidth = isPixels
    ? layout.values.reduce((sum, value) => sum + value, 0)
    : null;
  const title = table?.caption || table?.title;

  return (
    <div className={`book-table-scroll ${className}`.trim()}>
      <table
        className="book-content-table"
        style={
          isPixels
            ? { width: `${pixelWidth}px`, minWidth: `${pixelWidth}px` }
            : undefined
        }
      >
        <colgroup>
          {layout.values.map((width, index) => (
            <col
              // Column order is the stable identity in the legacy table JSON.
              key={index}
              style={{ width: `${width}${isPixels ? 'px' : '%'}` }}
            />
          ))}
        </colgroup>
        <tbody>
          {shaped.grid.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, columnIndex) => {
                const owner = ownership.get(`${rowIndex}:${columnIndex}`);
                if (
                  owner &&
                  (owner.row !== rowIndex || owner.column !== columnIndex)
                ) {
                  return null;
                }
                const isSelected =
                  selectedRange
                    ? rowIndex >= Math.min(selectedRange.startRow, selectedRange.endRow) &&
                      rowIndex <= Math.max(selectedRange.startRow, selectedRange.endRow) &&
                      columnIndex >= Math.min(selectedRange.startColumn, selectedRange.endColumn) &&
                      columnIndex <= Math.max(selectedRange.startColumn, selectedRange.endColumn)
                    : activeCell?.row === rowIndex &&
                      activeCell?.column === columnIndex;
                const cellStyle = getTableCellStyle(shaped, {
                  row: rowIndex,
                  column: columnIndex,
                });
                const richHtml = getTableCellRichHtml(shaped, {
                  row: rowIndex,
                  column: columnIndex,
                });
                const selectCell = (event) =>
                  onCellClick?.(
                    { row: rowIndex, column: columnIndex },
                    event
                  );

                return (
                  <td
                    key={columnIndex}
                    rowSpan={owner?.rowspan > 1 ? owner.rowspan : undefined}
                    colSpan={owner?.colspan > 1 ? owner.colspan : undefined}
                    aria-selected={onCellClick ? isSelected : undefined}
                    aria-label={
                      onCellClick
                        ? `Cellule ligne ${rowIndex + 1}, colonne ${columnIndex + 1}`
                        : undefined
                    }
                    className={isSelected ? 'book-table-cell--selected' : ''}
                    style={{
                      ...(cellStyle?.background_color
                        ? { backgroundColor: cellStyle.background_color }
                        : {}),
                      ...(cellStyle?.text_align
                        ? { textAlign: cellStyle.text_align }
                        : {}),
                    }}
                    onClick={onCellClick ? selectCell : undefined}
                    onKeyDown={
                      onCellClick
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              selectCell(event);
                            }
                          }
                        : undefined
                    }
                    tabIndex={onCellClick ? 0 : undefined}
                  >
                    <TableCellRichContent
                      html={richHtml}
                      fallbackText={cell}
                      className={
                        cellStyle?.text_direction === 'vertical-up'
                          ? 'book-table-cell-text--vertical-up'
                          : ''
                      }
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {title && <p className="book-table-caption">{title}</p>}
    </div>
  );
};

export default ContentTable;
